import React, { Dispatch, SetStateAction } from 'react';
import { Autocomplete, AutocompleteProps, CircularProgress, TextField } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type FormAutocompleteControllerProps<T extends { id: number }> = AutocompleteProps<
  T,
  true,
  false,
  undefined
> & {
  label: string;
  controllerName: string;
  options: T[];
  fetchOptions: (search: string) => UseQueryResult<T[], Error>;
  setOptions: Dispatch<SetStateAction<T[]>>;
  autocompleteId?: string;
  placeholder?: string;
  limitTags?: number;
  optionsToFilterOut?: T[];
  fieldOnBlur?: () => void;
};

export const FormAutocompleteController = <T extends { id: number }>({
  label,
  controllerName,
  options,
  fetchOptions,
  setOptions,
  placeholder,
  fieldOnBlur,
  optionsToFilterOut,
  ...props
}: FormAutocompleteControllerProps<T>) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const { control } = useFormContext();

  const { data, isLoading, isError } = fetchOptions('');

  const handleOpen = () => {
    setOpen(true);
    (async () => {
      setLoading(true);
      if (isError) {
        setLoading(false);
        enqueueSnackbar(t('ERRORS.LOADING_RESULTS_FAILURE'), {
          variant: 'error',
          autoHideDuration: 2000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        });
        return;
      }
      const filteredOptions = data?.filter(
        (o) => !optionsToFilterOut?.some((option) => option.id === o.id),
      );

      setOptions(filteredOptions ?? []);
      setLoading(false);
    })();
  };

  const handleClose = () => {
    setOpen(false);
    setOptions([]);
  };

  return (
    <Controller
      name={controllerName}
      control={control}
      render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
        <Autocomplete
          {...props}
          open={open}
          onOpen={handleOpen}
          onBlur={() => {
            onBlur();
            fieldOnBlur?.();
          }}
          onClose={handleClose}
          value={value ?? null}
          onChange={(_, data) => onChange(data)}
          options={options}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={ref}
              label={label}
              placeholder={placeholder}
              error={!!error}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading || isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
};
